import { isDraggableGridShapeType } from './addMoveMode';
import { canStartGridDragFromEvent } from './dragInput';
import type { GridDragInputEvent } from './dragInput';

export interface GridDragPoint {
  cx: number;
  cy: number;
  [key: string]: unknown;
}

export interface GridDragShape {
  t?: string;
  cx?: number;
  cy?: number;
  first?: GridDragPoint;
  second?: GridDragPoint;
  high?: boolean;
  path?: number[][] | null;
  sideways?: boolean;
  [key: string]: unknown;
}

export interface GridDragBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

export interface ResolveDraggedPointOptions {
  startMouse: GridDragPoint;
  rawMouse: GridDragPoint;
  startPoint: GridDragPoint;
  previousPoint: GridDragPoint;
  bounds: GridDragBounds;
  snapIncrement?: number;
  axisSnapThreshold?: number;
  anchorPoint?: GridDragPoint;
  parabolaThreshold?: number;
  shapeType?: string;
}

export interface ResolveDraggedShapeOptions {
  startMouse: GridDragPoint;
  rawMouse: GridDragPoint;
  startFirst: GridDragPoint;
  startSecond: GridDragPoint;
  bounds: GridDragBounds;
  shapeType?: string;
  snapIncrement?: number;
  axisSnapThreshold?: number;
}

export interface ResolvedDraggedShape {
  first: GridDragPoint;
  second: GridDragPoint;
  deltaX: number;
  deltaY: number;
}

export type ResolveGridDraggedShapeOptions = Omit<
  ResolveDraggedShapeOptions,
  'shapeType' | 'snapIncrement' | 'axisSnapThreshold'
> & {
  shape: Pick<GridDragShape, 't'>;
  grid: {
    args: {
      pixelsPerBlock: number;
      stg?: unknown;
      sta?: unknown;
    };
  };
  getter: {
    actingAsNumberLineGetter?: boolean;
  };
};

export interface GetGridRawPointFromLocalOptions {
  x: number;
  y: number;
  actingAsNumberLineGetter?: boolean;
}

export interface GetGridDragBoundsOptions {
  firstQuadrant?: boolean;
  viewportW: number;
  viewportSlideX?: number;
}

export type GridDragTarget =
  | {
      kind: 'point';
      obj: GridDragShape;
      point: GridDragPoint;
      pointKey: 'first' | 'second' | 'self';
    }
  | {
      kind: 'object';
      obj: GridDragShape;
    };

interface FindGridDragTargetOptions {
  pointRadius?: number;
  midpointRadius?: number;
  objectRadius?: number;
  allowObjectDrag?: boolean;
  inputEvent?: GridDragInputEvent;
  allowTouchInput?: boolean;
  preferredObject?: GridDragShape;
}

const MAX_PATH_HIT_TEST_SEGMENTS = 600;

type GridDragDot = GridDragShape & GridDragPoint & { t: 'dot' };

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

const distance = (x1: number, y1: number, x2: number, y2: number): number =>
  Math.hypot(x1 - x2, y1 - y2);

const distancesTie = (first: number, second: number): boolean =>
  Math.abs(first - second) < 0.000001;

const distanceToLine = (
  x: number,
  y: number,
  first: GridDragPoint,
  second: GridDragPoint
): number => {
  const dx = second.cx - first.cx;
  const dy = second.cy - first.cy;
  const length = Math.hypot(dx, dy);

  if (length === 0) {
    return distance(x, y, first.cx, first.cy);
  }

  return (
    Math.abs(dy * x - dx * y + second.cx * first.cy - second.cy * first.cx) /
    length
  );
};

const distanceToSegmentCoordinates = (
  x: number,
  y: number,
  firstX: number,
  firstY: number,
  secondX: number,
  secondY: number
): number => {
  const dx = secondX - firstX;
  const dy = secondY - firstY;
  const lengthSquared = dx * dx + dy * dy;

  if (lengthSquared === 0) {
    return distance(x, y, firstX, firstY);
  }

  const projection = clamp(
    ((x - firstX) * dx + (y - firstY) * dy) / lengthSquared,
    0,
    1
  );

  return distance(x, y, firstX + projection * dx, firstY + projection * dy);
};

const distanceToSegment = (
  x: number,
  y: number,
  first: GridDragPoint,
  second: GridDragPoint
): number =>
  distanceToSegmentCoordinates(x, y, first.cx, first.cy, second.cx, second.cy);

const distanceToRay = (
  x: number,
  y: number,
  first: GridDragPoint,
  second: GridDragPoint
): number => {
  const dx = second.cx - first.cx;
  const dy = second.cy - first.cy;
  const lengthSquared = dx * dx + dy * dy;

  if (lengthSquared === 0) {
    return distance(x, y, first.cx, first.cy);
  }

  const projection =
    ((x - first.cx) * dx + (y - first.cy) * dy) / lengthSquared;

  if (projection < 0) {
    return Number.POSITIVE_INFINITY;
  }

  return distance(x, y, first.cx + projection * dx, first.cy + projection * dy);
};

const distanceToPath = (
  x: number,
  y: number,
  path: number[][],
  sideways?: boolean
): number => {
  if (path.length === 0) {
    return Number.POSITIVE_INFINITY;
  }

  // Sideways paths are rotated 90 degrees when painted. Inverse-rotating the
  // pointer keeps hit testing in the path's original coordinate system.
  const pathX = sideways ? y : x;
  const pathY = sideways ? -x : y;
  const pathStep = Math.max(
    1,
    Math.ceil((path.length - 1) / MAX_PATH_HIT_TEST_SEGMENTS)
  );
  let previous = path[0];
  let closestDistance = distance(pathX, pathY, previous[0], previous[1]);

  for (let index = pathStep; index < path.length; index += pathStep) {
    const current = path[index];

    closestDistance = Math.min(
      closestDistance,
      distanceToSegmentCoordinates(
        pathX,
        pathY,
        previous[0],
        previous[1],
        current[0],
        current[1]
      )
    );
    previous = current;
  }

  const last = path[path.length - 1];

  if (previous !== last) {
    closestDistance = Math.min(
      closestDistance,
      distanceToSegmentCoordinates(
        pathX,
        pathY,
        previous[0],
        previous[1],
        last[0],
        last[1]
      )
    );
  }

  return closestDistance;
};

const distanceToObjectBody = (
  x: number,
  y: number,
  obj: GridDragShape
): number => {
  if (!obj.first || !obj.second) {
    return Number.POSITIVE_INFINITY;
  }

  switch (obj.t) {
    case 'Line':
      return distanceToLine(x, y, obj.first, obj.second);
    case 'Ray':
      return distanceToRay(x, y, obj.first, obj.second);
    case 'Segment':
      return distanceToSegment(x, y, obj.first, obj.second);
    case 'Parabola':
      if (obj.path && obj.path.length > 0) {
        return distanceToPath(x, y, obj.path, obj.sideways);
      }

      return distance(
        x,
        y,
        (obj.first.cx + obj.second.cx) / 2,
        (obj.first.cy + obj.second.cy) / 2
      );
    default:
      return Number.POSITIVE_INFINITY;
  }
};

const isSlopeNearlyVertical = (slope: number): boolean =>
  Math.abs(slope) >= 120 / 7;

const isNearlyHorizontal = (slope: number): boolean =>
  Math.abs(slope) <= 7 / 120;

const roundToHundredth = (value: number): number =>
  Math.round(value * 100) / 100;

const roundToIncrement = (value: number, increment: number): number =>
  Math.round(value / increment) * increment;

type ShapeAxisSnapEndpoints = 'first' | 'both';

const getShapeAxisSnapEndpoints = (
  shapeType: string | undefined
): ShapeAxisSnapEndpoints | undefined => {
  if (shapeType === 'Ray') {
    return 'first';
  }

  if (shapeType === 'Segment') {
    return 'both';
  }

  return undefined;
};

const snapTranslationToAxis = (
  delta: number,
  firstCoordinate: number,
  secondCoordinate: number,
  threshold: number | undefined,
  eligibleEndpoints: ShapeAxisSnapEndpoints | undefined
): number => {
  if (threshold == null || eligibleEndpoints == null) {
    return delta;
  }

  const firstDistance = Math.abs(firstCoordinate + delta);
  const secondDistance =
    eligibleEndpoints === 'both'
      ? Math.abs(secondCoordinate + delta)
      : Number.POSITIVE_INFINITY;
  const closestDistance = Math.min(firstDistance, secondDistance);

  if (closestDistance > threshold) {
    return delta;
  }

  const snappedDelta =
    firstDistance <= secondDistance ? -firstCoordinate : -secondCoordinate;

  return snappedDelta === 0 ? 0 : snappedDelta;
};

const isCompleteDraggableShape = (obj: GridDragShape | undefined): boolean =>
  Boolean(obj && isDraggableGridShapeType(obj.t) && obj.first && obj.second);

const isDraggablePointDot = (
  obj: GridDragShape | undefined
): obj is GridDragDot =>
  Boolean(
    obj &&
      obj.t === 'dot' &&
      !obj.high &&
      typeof obj.cx === 'number' &&
      typeof obj.cy === 'number'
  );

const isDraggableGridObject = (obj: GridDragShape | undefined): boolean =>
  isCompleteDraggableShape(obj) || isDraggablePointDot(obj);

export const hasDraggableGridObjects = (
  objects: GridDragShape[] | undefined
): boolean => Boolean(objects?.some(isDraggableGridObject));

export const getGridRawPointFromLocal = ({
  x,
  y,
  actingAsNumberLineGetter = false,
}: GetGridRawPointFromLocalOptions): GridDragPoint => {
  const point = {
    cx: roundToHundredth(x),
    cy: roundToHundredth(-y),
  };

  if (actingAsNumberLineGetter) {
    point.cy = 0;
  }

  return point;
};

export const getGridDragBounds = ({
  firstQuadrant = false,
  viewportW,
  viewportSlideX = 0,
}: GetGridDragBoundsOptions): GridDragBounds => {
  if (firstQuadrant) {
    const max = viewportW - viewportSlideX;

    return {
      minX: 0,
      maxX: max,
      minY: 0,
      maxY: max,
    };
  }

  const max = viewportW / 2;

  return {
    minX: -max,
    maxX: max,
    minY: -max,
    maxY: max,
  };
};

export const isGridRawPointWithinDragBounds = (
  point: GridDragPoint,
  bounds: GridDragBounds
): boolean =>
  point.cx >= bounds.minX &&
  point.cx <= bounds.maxX &&
  point.cy >= bounds.minY &&
  point.cy <= bounds.maxY;

export const findGridDragTarget = (
  objects: GridDragShape[] | undefined,
  x: number,
  y: number,
  options?: FindGridDragTargetOptions
): GridDragTarget | undefined => {
  if (
    !canStartGridDragFromEvent(options?.inputEvent, {
      allowTouchInput: options?.allowTouchInput,
    })
  ) {
    return undefined;
  }

  if (!objects || objects.length === 0) {
    return undefined;
  }

  const pointRadius = options?.pointRadius ?? 10;
  const objectRadius = options?.objectRadius ?? options?.midpointRadius ?? 12;
  const preferredObject = options?.preferredObject;
  let pointTarget:
    | (GridDragTarget & {
        distance: number;
        preferred: boolean;
      })
    | undefined;

  for (let index = objects.length - 1; index >= 0; index--) {
    const obj = objects[index];

    if (isDraggablePointDot(obj)) {
      const pointDistance = distance(x, y, obj.cx, obj.cy);
      const preferred = obj === preferredObject;

      if (
        pointDistance <= pointRadius &&
        (!pointTarget ||
          pointDistance < pointTarget.distance ||
          (preferred &&
            !pointTarget.preferred &&
            distancesTie(pointDistance, pointTarget.distance)))
      ) {
        pointTarget = {
          kind: 'point',
          obj,
          point: obj,
          pointKey: 'self',
          distance: pointDistance,
          preferred,
        };
      }

      continue;
    }

    if (!isCompleteDraggableShape(obj)) {
      continue;
    }

    (['first', 'second'] as const).forEach(pointKey => {
      const point = obj[pointKey];

      if (!point) {
        return;
      }

      const pointDistance = distance(x, y, point.cx, point.cy);
      const preferred = obj === preferredObject;

      if (
        pointDistance <= pointRadius &&
        (!pointTarget ||
          pointDistance < pointTarget.distance ||
          (preferred &&
            !pointTarget.preferred &&
            distancesTie(pointDistance, pointTarget.distance)))
      ) {
        pointTarget = {
          kind: 'point',
          obj,
          point,
          pointKey,
          distance: pointDistance,
          preferred,
        };
      }
    });
  }

  if (pointTarget) {
    const {
      distance: _distance,
      preferred: _preferred,
      ...target
    } = pointTarget;

    return target;
  }

  if (!options?.allowObjectDrag) {
    return undefined;
  }

  let objectTarget:
    | (GridDragTarget & {
        distance: number;
        preferred: boolean;
      })
    | undefined;

  for (let index = objects.length - 1; index >= 0; index--) {
    const obj = objects[index];

    if (!isCompleteDraggableShape(obj)) {
      continue;
    }

    const bodyDistance = distanceToObjectBody(x, y, obj);
    const preferred = obj === preferredObject;

    if (
      bodyDistance <= objectRadius &&
      (!objectTarget ||
        bodyDistance < objectTarget.distance ||
        (preferred &&
          !objectTarget.preferred &&
          distancesTie(bodyDistance, objectTarget.distance)))
    ) {
      objectTarget = {
        kind: 'object',
        obj,
        distance: bodyDistance,
        preferred,
      };
    }
  }

  if (!objectTarget) {
    return undefined;
  }

  const {
    distance: _distance,
    preferred: _preferred,
    ...target
  } = objectTarget;

  return target;
};

export const removeDuplicateGridPointAtTarget = (
  objects: GridDragShape[] | undefined,
  target: GridDragTarget | undefined
): boolean => {
  if (
    !objects ||
    !target ||
    target.kind !== 'point' ||
    target.pointKey !== 'self' ||
    !isDraggablePointDot(target.obj)
  ) {
    return false;
  }

  if (objects.indexOf(target.obj) === -1) {
    return false;
  }

  let removedDuplicate = false;

  for (let index = objects.length - 1; index >= 0; index--) {
    const obj = objects[index];

    if (
      obj === target.obj ||
      obj.t !== 'dot' ||
      obj.cx !== target.point.cx ||
      obj.cy !== target.point.cy
    ) {
      continue;
    }

    objects.splice(index, 1);
    removedDuplicate = true;
  }

  return removedDuplicate;
};

export const resolveDraggedPoint = ({
  startMouse,
  rawMouse,
  startPoint,
  previousPoint,
  bounds,
  snapIncrement,
  axisSnapThreshold,
  anchorPoint,
  parabolaThreshold,
  shapeType,
}: ResolveDraggedPointOptions): GridDragPoint => {
  const deltaX = rawMouse.cx - startMouse.cx;
  const deltaY = rawMouse.cy - startMouse.cy;

  if (deltaX === 0 && deltaY === 0) {
    return {
      cx: startPoint.cx,
      cy: startPoint.cy,
    };
  }

  const nextPoint = {
    cx: roundToHundredth(startPoint.cx + deltaX),
    cy: roundToHundredth(startPoint.cy + deltaY),
  };

  if (snapIncrement) {
    nextPoint.cx = roundToIncrement(nextPoint.cx, snapIncrement);
    nextPoint.cy = roundToIncrement(nextPoint.cy, snapIncrement);
  }

  if (axisSnapThreshold != null) {
    if (Math.abs(nextPoint.cx) <= axisSnapThreshold) {
      nextPoint.cx = 0;
    }

    if (Math.abs(nextPoint.cy) <= axisSnapThreshold) {
      nextPoint.cy = 0;
    }
  }

  if (anchorPoint && shapeType !== 'Parabola') {
    const pointDistance = distance(
      anchorPoint.cx,
      anchorPoint.cy,
      nextPoint.cx,
      nextPoint.cy
    );

    if (
      pointDistance >= 10 &&
      nextPoint.cx !== anchorPoint.cx &&
      nextPoint.cy !== anchorPoint.cy
    ) {
      const slope =
        (nextPoint.cy - anchorPoint.cy) / (nextPoint.cx - anchorPoint.cx);

      if (isSlopeNearlyVertical(slope)) {
        nextPoint.cx = anchorPoint.cx;
      } else if (isNearlyHorizontal(slope)) {
        nextPoint.cy = anchorPoint.cy;
      }
    }
  }

  nextPoint.cx = clamp(nextPoint.cx, bounds.minX, bounds.maxX);
  nextPoint.cy = clamp(nextPoint.cy, bounds.minY, bounds.maxY);

  if (
    shapeType === 'Parabola' &&
    anchorPoint &&
    parabolaThreshold != null &&
    (Math.round(Math.abs(anchorPoint.cx - nextPoint.cx)) <= parabolaThreshold ||
      Math.round(Math.abs(anchorPoint.cy - nextPoint.cy)) <= parabolaThreshold)
  ) {
    return {
      cx: previousPoint.cx,
      cy: previousPoint.cy,
    };
  }

  return nextPoint;
};

export const resolveDraggedShape = ({
  startMouse,
  rawMouse,
  startFirst,
  startSecond,
  bounds,
  shapeType,
  snapIncrement,
  axisSnapThreshold,
}: ResolveDraggedShapeOptions): ResolvedDraggedShape => {
  const rawDeltaX = rawMouse.cx - startMouse.cx;
  const rawDeltaY = rawMouse.cy - startMouse.cy;
  let deltaX = rawDeltaX;
  let deltaY = rawDeltaY;

  if (snapIncrement) {
    deltaX = roundToIncrement(deltaX, snapIncrement);
    deltaY = roundToIncrement(deltaY, snapIncrement);
  }

  if (rawDeltaX !== 0 || rawDeltaY !== 0) {
    const eligibleEndpoints = getShapeAxisSnapEndpoints(shapeType);

    deltaX = snapTranslationToAxis(
      deltaX,
      startFirst.cx,
      startSecond.cx,
      axisSnapThreshold,
      eligibleEndpoints
    );
    deltaY = snapTranslationToAxis(
      deltaY,
      startFirst.cy,
      startSecond.cy,
      axisSnapThreshold,
      eligibleEndpoints
    );
  }

  const minDeltaX = Math.max(
    bounds.minX - startFirst.cx,
    bounds.minX - startSecond.cx
  );
  const maxDeltaX = Math.min(
    bounds.maxX - startFirst.cx,
    bounds.maxX - startSecond.cx
  );
  const minDeltaY = Math.max(
    bounds.minY - startFirst.cy,
    bounds.minY - startSecond.cy
  );
  const maxDeltaY = Math.min(
    bounds.maxY - startFirst.cy,
    bounds.maxY - startSecond.cy
  );

  deltaX = clamp(deltaX, minDeltaX, maxDeltaX);
  deltaY = clamp(deltaY, minDeltaY, maxDeltaY);

  return {
    first: {
      cx: startFirst.cx + deltaX,
      cy: startFirst.cy + deltaY,
    },
    second: {
      cx: startSecond.cx + deltaX,
      cy: startSecond.cy + deltaY,
    },
    deltaX,
    deltaY,
  };
};

export const resolveGridDraggedShape = ({
  shape,
  grid,
  getter,
  ...shapeOptions
}: ResolveGridDraggedShapeOptions): ResolvedDraggedShape =>
  resolveDraggedShape({
    ...shapeOptions,
    shapeType: shape.t,
    snapIncrement: grid.args.stg ? grid.args.pixelsPerBlock : undefined,
    axisSnapThreshold:
      grid.args.sta && !getter.actingAsNumberLineGetter
        ? grid.args.pixelsPerBlock / 2
        : undefined,
  });
